/**
 * Origin Display Utility
 *
 * Provides consistent origin/source display logic across all tables (Assets, Seeds, Vulnerabilities).
 * Implements the Chariot rollup pattern where native capabilities display as "Chariot"
 * while preserving integration names and manual imports.
 *
 * NSA/CISA Consolidation (Added 2025-12-29):
 * - NSA CSaaS integrations (assets-import + vulns-import) → "NSA CSaaS"
 * - CISA CH integrations (vulnscan-import + webappscan-import) → "CISA CH"
 * - Consolidation applies to both display (table cells) and filters (dropdown options)
 * - Filter selections are expanded back to actual integration IDs for backend queries
 *
 * Pattern Reference: /technology-refactor/Origin-Display-Pattern-Analysis.md
 * Backend Authority: Capability.Integration() method determines native vs third-party
 */

import { AllIntegrations } from '@/hooks/useIntegration';
import type { DropdownOption } from '@/types/dropdown.types';
import { normalizeOriginName } from '@/utils/statisticValidation.util';

/**
 * Extended dropdown option with group support
 */
export type GroupOption = DropdownOption & {
  isGroup?: boolean;
  items?: DropdownOption[];
};

/**
 * NSA CSaaS Integration IDs
 * Both asset and vulnerability imports consolidate to "NSA CSaaS" display name
 */
export const NSA_CSAAS_INTEGRATIONS = [
  'nsa-csaas-assets-import',
  'nsa-csaas-vulns-import',
] as const;

/**
 * CISA Cyber Hygiene Integration IDs
 * Both vulnerability and web app scans consolidate to "CISA CH" display name
 */
export const CISA_CH_INTEGRATIONS = [
  'cisa-ch-vulnscan-import',
  'cisa-ch-webappscan-import',
] as const;

/**
 * Consolidated display names
 */
export const NSA_CSAAS_DISPLAY = 'NSA CSaaS';
export const CISA_CH_DISPLAY = 'CISA CH';

/**
 * Virtual filter values for consolidated options
 * These are used in filter dropdowns and expanded to actual integration IDs
 */
export const NSA_CSAAS_FILTER_VALUE = 'nsa-csaas';
export const CISA_CH_FILTER_VALUE = 'cisa-ch';

/** Sorts display origin labels alphabetically with 'Praetorian' always first. */
export const praetorianFirstComparator = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a === 'Praetorian') return -1;
  if (b === 'Praetorian') return 1;
  return a.localeCompare(b);
};

export const SPECIAL_ORIGIN_DISPLAY_LABELS: Record<string, string> = {
  constantine: 'Constantine',
  secrets: 'Titus',
  'aurelian-agent': 'Aurelian',
  'romulus-agent': 'Romulus',
  trajan: 'Trajan',
  vespasian: 'Vespasian',
  julius: 'Julius',
  caligula: 'Caligula',
  brutus: 'Brutus',
  augustus: 'Augustus',
  hunt: 'Hannibal',
};

/**
 * Backend origin values that are variants of a base integration. Autotriaged
 * HackerOne risks carry the `hackerone-auto-triage` origin (with a `hackerone`
 * source) rather than a plain `hackerone` origin, so both the Origin column and
 * the Origin filter must treat the variant as its base integration. Maps a base
 * integration id to the variant origin values that should roll up to it.
 */
export const INTEGRATION_ORIGIN_VARIANTS: Record<string, string[]> = {
  hackerone: ['hackerone-auto-triage'],
};

/** Reverse of INTEGRATION_ORIGIN_VARIANTS: variant origin → base integration id. */
const ORIGIN_INTEGRATION_ALIASES: Record<string, string> = Object.fromEntries(
  Object.entries(INTEGRATION_ORIGIN_VARIANTS).flatMap(([base, variants]) =>
    variants.map(variant => [variant.toLowerCase(), base])
  )
);

/**
 * Expands selected origin filter values to also include any backend origin
 * variants that should match them. Selecting `hackerone` must also match
 * autotriaged risks whose only origin is `hackerone-auto-triage`.
 *
 * @param origins - Selected origin filter values
 * @returns The values plus any variant origins, de-duplicated
 */
export const expandOriginFilterVariants = (origins: string[]): string[] => {
  const expanded = new Set(origins);
  for (const origin of origins) {
    const variants = INTEGRATION_ORIGIN_VARIANTS[origin.toLowerCase()];
    if (variants) {
      for (const variant of variants) {
        expanded.add(variant);
      }
    }
  }
  return Array.from(expanded);
};

/**
 * Gets display text for multiple origins (used by Assets, Seeds, Vulnerabilities tables).
 * Applies rollup logic to each origin and returns a deduplicated, sorted array.
 *
 * Ordering: alphabetical (localeCompare) with 'Praetorian' promoted to the FRONT.
 * This is the canonical single-tier order used when no origin filters are active;
 * {@link orderOriginsForDisplay} layers a filter-match tier on top of it.
 *
 * @param rawOrigins - Array of raw origin values
 * @param sourceFilterOptions - Filter options from useGetVulnerabilityFilters
 * @returns Array of display-ready origin strings ('Praetorian' first, then alphabetical)
 *
 * @example
 * ```typescript
 * const origins = ['github', 'subdomain-enumerator', 'amazon'];
 * getDisplayOriginTexts(origins, sourceFilterOptions)
 * // → ["Praetorian", "AWS", "GitHub"] (native capability rolled up, Praetorian first)
 * ```
 */
export const getDisplayOriginTexts = (
  rawOrigins: (string | undefined | null)[],
  sourceFilterOptions: GroupOption[]
): string[] => {
  // Filter out null/undefined values
  const validOrigins = rawOrigins.filter(
    (origin): origin is string => !!origin
  );

  // Apply rollup logic to each origin
  const displayOrigins = validOrigins.map(origin =>
    getDisplayOriginText(origin, sourceFilterOptions)
  );

  // Deduplicate using Set
  const result = Array.from(new Set(displayOrigins));

  // Sort so that "Praetorian" is always first
  result.sort(praetorianFirstComparator);

  return result;
};

/**
 * Maps raw origin filter values (as sent to the backend) to their display labels.
 *
 * Reuses {@link getDisplayOriginText} for integration ids ('github', 'amazon', …)
 * and native capability ids (both roll up to 'Praetorian'); emails ending in
 * '@praetorian.com' map to 'Import'. The virtual consolidated filter values
 * ('nsa-csaas' / 'cisa-ch') are handled here BEFORE delegating, because
 * {@link getDisplayOriginText} does not recognize them and would otherwise fall
 * through to 'Praetorian'.
 *
 * The returned labels are deduplicated. Inputs are not mutated.
 *
 * @param filterValues - Active origin filter values (raw backend values)
 * @param sourceFilterOptions - Filter options containing the Praetorian group + integration labels
 * @returns Deduplicated array of display labels for the given filter values
 *
 * @example
 * ```typescript
 * mapOriginFilterValuesToDisplayLabels(['github', 'nsa-csaas'], sourceFilterOptions)
 * // → ['GitHub', 'NSA CSaaS']
 * ```
 */
export const mapOriginFilterValuesToDisplayLabels = (
  filterValues: string[],
  sourceFilterOptions: GroupOption[]
): string[] => {
  const labels = filterValues.map(value => {
    const lowerValue = value.toLowerCase();
    // Virtual consolidated filter values are not understood by getDisplayOriginText.
    if (lowerValue === NSA_CSAAS_FILTER_VALUE) return NSA_CSAAS_DISPLAY;
    if (lowerValue === CISA_CH_FILTER_VALUE) return CISA_CH_DISPLAY;
    return getDisplayOriginText(value, sourceFilterOptions);
  });

  return Array.from(new Set(labels));
};

/**
 * Tier-orders display labels against a set of already-mapped "matched" labels.
 *
 * This is the shared core of the origin-chip ordering. When `matchedLabels` is
 * empty/undefined there is a single tier: alphabetical with 'Praetorian' first.
 * Otherwise labels are split into a matched tier (label appears in
 * `matchedLabels`, case-insensitively) followed by an unmatched tier; each tier
 * is independently sorted alphabetically with 'Praetorian' promoted to its front.
 *
 * Exported so consumers that apply their own label remap before matching (e.g.
 * the Vulnerabilities cell, which remaps 'Import' → 'Manual'/'Praetorian') can
 * reuse the exact tier-sort logic instead of duplicating it. Inputs are not mutated.
 *
 * @param displayOrigins - Final display labels to order
 * @param matchedLabels - Already-mapped display labels considered filter matches
 * @returns New, ordered array of display labels
 */
export const orderOriginsByMatchedLabels = (
  displayOrigins: string[],
  matchedLabels: string[] | undefined
): string[] => {
  // No matches → single tier: alphabetical with 'Praetorian' first.
  if (!matchedLabels || matchedLabels.length === 0) {
    return [...displayOrigins].sort(praetorianFirstComparator);
  }

  const matchedSet = new Set(matchedLabels.map(label => label.toLowerCase()));

  const matched: string[] = [];
  const unmatched: string[] = [];
  for (const origin of displayOrigins) {
    if (matchedSet.has(origin.toLowerCase())) {
      matched.push(origin);
    } else {
      unmatched.push(origin);
    }
  }

  matched.sort(praetorianFirstComparator);
  unmatched.sort(praetorianFirstComparator);

  return [...matched, ...unmatched];
};

/**
 * Orders origin display labels for chip rendering (ChipsList with maxVisible=1).
 *
 * Rules:
 * 1. Filter-match tier first: when origin filters are active, labels that match an
 *    active filter come before those that don't.
 * 2. Within each tier: alphabetical (localeCompare) EXCEPT 'Praetorian' is always
 *    promoted to the FRONT of its tier.
 * 3. No active filters → single tier: alphabetical with 'Praetorian' first
 *    (equivalent to {@link getDisplayOriginTexts}'s ordering).
 *
 * Filter values are raw backend values, not display labels — they are mapped via
 * {@link mapOriginFilterValuesToDisplayLabels} before matching (case-insensitive).
 * Inputs are not mutated.
 *
 * @param displayOrigins - Display labels (already rolled up + deduplicated)
 * @param activeOriginFilters - Active origin filter values (raw backend values), or undefined
 * @param sourceFilterOptions - Filter options used to map filter values to labels
 * @returns New, ordered array of display labels
 *
 * @example
 * ```typescript
 * // No filters
 * orderOriginsForDisplay(['GitHub', 'Praetorian', 'AWS'], undefined, opts)
 * // → ['Praetorian', 'AWS', 'GitHub']
 *
 * // Filter on github + aws → matched tier first
 * orderOriginsForDisplay(['GitHub', 'Praetorian', 'AWS'], ['github', 'amazon'], opts)
 * // → ['AWS', 'GitHub', 'Praetorian']
 * ```
 */
export const orderOriginsForDisplay = (
  displayOrigins: string[],
  activeOriginFilters: string[] | undefined,
  sourceFilterOptions: GroupOption[]
): string[] => {
  const matchedLabels =
    activeOriginFilters && activeOriginFilters.length > 0
      ? mapOriginFilterValuesToDisplayLabels(
          activeOriginFilters,
          sourceFilterOptions
        )
      : undefined;

  return orderOriginsByMatchedLabels(displayOrigins, matchedLabels);
};

/**
 * Gets display text for origin/source values using Chariot rollup pattern.
 *
 * Display Priority:
 * 1. Manual imports (emails ending in @praetorian.com) → "Import"
 * 2. Chariot native capabilities (from Chariot group) → "Chariot"
 * 3. Third-party integrations (github, aws, qualys, etc.) → Formatted integration name
 * 4. Unknown sources → "Chariot" (fallback)
 *
 * @param rawOrigin - Raw origin/source value from backend (e.g., "subdomain-enumerator", "github", "user@praetorian.com")
 * @param sourceFilterOptions - Filter options from useGetVulnerabilityFilters containing Chariot group
 * @returns Human-readable display text
 *
 * @example
 * ```typescript
 * // Chariot native capabilities
 * getDisplayOriginText('subdomain-enumerator', sourceFilterOptions) // → "Chariot"
 * getDisplayOriginText('portscanner', sourceFilterOptions) // → "Chariot"
 * getDisplayOriginText('crawler', sourceFilterOptions) // → "Chariot"
 *
 * // Third-party integrations
 * getDisplayOriginText('github', sourceFilterOptions) // → "GitHub"
 * getDisplayOriginText('amazon', sourceFilterOptions) // → "AWS"
 * getDisplayOriginText('qualys', sourceFilterOptions) // → "Qualys"
 *
 * // Manual imports
 * getDisplayOriginText('user@praetorian.com', sourceFilterOptions) // → "Import"
 *
 * // Special Chariot sources
 * getDisplayOriginText('provided', sourceFilterOptions) // → "Chariot"
 * getDisplayOriginText('adhoc', sourceFilterOptions) // → "Chariot"
 * ```
 */
export const getDisplayOriginText = (
  rawOrigin: string | undefined | null,
  sourceFilterOptions: GroupOption[]
): string => {
  // Handle empty/null values
  if (!rawOrigin) {
    return 'Praetorian';
  }

  // Roll integration origin variants (e.g. hackerone-auto-triage) up to their
  // base integration id so the Origin column matches the Origin filter.
  const rawLower = rawOrigin.toLowerCase();
  const lowerOrigin = ORIGIN_INTEGRATION_ALIASES[rawLower] ?? rawLower;

  // Priority 1: Manual user imports
  // Any email ending in @praetorian.com is a manual entry
  const isManual = rawOrigin.endsWith('@praetorian.com');
  if (isManual) {
    return 'Import';
  }

  const specialOriginLabel = SPECIAL_ORIGIN_DISPLAY_LABELS[lowerOrigin];
  if (specialOriginLabel) {
    return specialOriginLabel;
  }

  // Priority 2: Praetorian native capabilities
  // Extract Praetorian group from filter options and check membership
  const chariotGroup = sourceFilterOptions.find(
    sf => sf.isGroup && sf.label === 'Praetorian'
  );
  const chariotSourceValues =
    chariotGroup?.items?.map(item => String(item.value).toLowerCase()) || [];

  const isChariotGroupedSource = chariotSourceValues.includes(lowerOrigin);
  if (isChariotGroupedSource) {
    return 'Praetorian';
  }

  // Priority 2.5: NSA/CISA consolidation (NEW)
  const consolidatedName = getConsolidatedDisplayName(rawOrigin);
  if (consolidatedName) {
    return consolidatedName;
  }

  // Priority 3: Third-party integrations
  // Check if source is a known integration
  const isIntegration = Object.keys(AllIntegrations).includes(lowerOrigin);
  if (isIntegration) {
    return getFormattedLabel(lowerOrigin, sourceFilterOptions);
  }

  // Fallback: Default to "Praetorian" for unknown sources
  // This handles edge cases like legacy data or special system sources
  return 'Praetorian';
};

/**
 * Gets formatted label for a source value from filter options.
 * Falls back to normalizeOriginName if not found in filter options.
 *
 * @param sourceValue - Raw source value
 * @param sourceFilterOptions - Filter options containing label mappings
 * @returns Formatted label for display
 */
const getFormattedLabel = (
  sourceValue: string,
  sourceFilterOptions: GroupOption[]
): string => {
  let foundLabel: string | undefined;

  // First check if it's in the Praetorian group items
  const chariotGroupFromFilters = sourceFilterOptions.find(
    sf => sf.isGroup && sf.label === 'Praetorian'
  );
  if (chariotGroupFromFilters?.items) {
    foundLabel = chariotGroupFromFilters.items.find(
      item => String(item.value).toLowerCase() === sourceValue.toLowerCase()
    )?.label;
  }

  // If not found, check top-level integration options
  if (!foundLabel) {
    const integrationOption = sourceFilterOptions.find(
      option =>
        !option.isGroup &&
        String(option.value).toLowerCase() === sourceValue.toLowerCase()
    );
    foundLabel = integrationOption?.label;
  }

  // Fallback to normalizeOriginName for formatting
  return foundLabel || normalizeOriginName(sourceValue);
};

/**
 * Gets consolidated display name for NSA/CISA integrations.
 * Returns null for integrations that don't need consolidation.
 *
 * @param integrationId - Integration ID to check
 * @returns Consolidated display name or null
 *
 * @example
 * getConsolidatedDisplayName('nsa-csaas-assets-import') // → "NSA CSaaS"
 * getConsolidatedDisplayName('github') // → null
 */
export const getConsolidatedDisplayName = (
  integrationId: string | undefined | null
): string | null => {
  if (!integrationId) {
    return null;
  }

  const lowerIntegrationId = integrationId.toLowerCase();

  // Check NSA CSaaS integrations
  if (
    NSA_CSAAS_INTEGRATIONS.map(id => id.toLowerCase()).includes(
      lowerIntegrationId
    )
  ) {
    return NSA_CSAAS_DISPLAY;
  }

  // Check CISA CH integrations
  if (
    CISA_CH_INTEGRATIONS.map(id => id.toLowerCase()).includes(
      lowerIntegrationId
    )
  ) {
    return CISA_CH_DISPLAY;
  }

  return null;
};

/**
 * Collapses expanded integration IDs back to consolidated filter values.
 * Used when displaying selected filters in dropdowns - shows consolidated
 * names instead of individual integration IDs.
 *
 * @param originValues - Array of origin filter values (possibly expanded)
 * @returns Array with consolidated values replacing expanded IDs
 *
 * @example
 * collapseOriginValues(['nsa-csaas-assets-import', 'nsa-csaas-vulns-import', 'github'])
 * // → ['nsa-csaas', 'github']
 *
 * collapseOriginValues(['github', 'aws'])
 * // → ['github', 'aws'] (no consolidation needed)
 */
export const collapseOriginValues = (originValues: string[]): string[] => {
  const result = new Set<string>();
  const processedIds = new Set<string>();

  for (const value of originValues) {
    const lowerValue = value.toLowerCase();

    // Skip if already processed as part of a consolidated group
    if (processedIds.has(lowerValue)) {
      continue;
    }

    // Check if this is part of NSA CSaaS group
    const nsaIds = NSA_CSAAS_INTEGRATIONS.map(id => id.toLowerCase());
    if (nsaIds.includes(lowerValue)) {
      result.add(NSA_CSAAS_FILTER_VALUE);
      // Mark all NSA IDs as processed
      nsaIds.forEach(id => processedIds.add(id));
      continue;
    }

    // Check if this is part of CISA CH group
    const cisaIds = CISA_CH_INTEGRATIONS.map(id => id.toLowerCase());
    if (cisaIds.includes(lowerValue)) {
      result.add(CISA_CH_FILTER_VALUE);
      // Mark all CISA IDs as processed
      cisaIds.forEach(id => processedIds.add(id));
      continue;
    }

    // Not part of a consolidated group - add as-is
    result.add(value);
    processedIds.add(lowerValue);
  }

  return Array.from(result);
};

/**
 * Expands consolidated filter value to actual integration IDs.
 * Used when applying filters - consolidated values need to be expanded
 * to query for all underlying integrations.
 *
 * @param consolidatedValue - The virtual filter value ("nsa-csaas" or "cisa-ch")
 * @returns Array of integration IDs or null if not a consolidated value
 *
 * @example
 * getConsolidatedOriginIds('nsa-csaas')
 * // → ['nsa-csaas-assets-import', 'nsa-csaas-vulns-import']
 *
 * getConsolidatedOriginIds('github') // → null (not consolidated)
 */
export const getConsolidatedOriginIds = (
  consolidatedValue: string | undefined | null
): string[] | null => {
  if (!consolidatedValue) {
    return null;
  }

  const lowerValue = consolidatedValue.toLowerCase();

  if (lowerValue === NSA_CSAAS_FILTER_VALUE) {
    return [...NSA_CSAAS_INTEGRATIONS];
  }

  if (lowerValue === CISA_CH_FILTER_VALUE) {
    return [...CISA_CH_INTEGRATIONS];
  }

  return null;
};

/**
 * Transforms source filter options to consolidate NSA/CISA integrations.
 *
 * Removes individual NSA/CISA integration options and replaces them with
 * consolidated options that users can select in filter dropdowns.
 *
 * @param options - Raw source filter options from useGetVulnerabilityFilters
 * @returns Transformed options with consolidated NSA/CISA entries
 *
 * @example
 * const raw = [
 *   { label: 'NSA CSaaS Attack Surface Management (Assets)', value: 'nsa-csaas-assets-import' },
 *   { label: 'NSA CSaaS Attack Surface Management (Vulnerabilities)', value: 'nsa-csaas-vulns-import' },
 *   { label: 'GitHub', value: 'github' },
 * ];
 * const transformed = transformSourceFilterOptions(raw);
 * // → [{ label: 'NSA CSaaS', value: 'nsa-csaas' }, { label: 'GitHub', value: 'github' }]
 */
export const transformSourceFilterOptions = (
  options: (DropdownOption | GroupOption)[]
): (DropdownOption | GroupOption)[] => {
  const result: (DropdownOption | GroupOption)[] = [];
  let hasNSA = false;
  let hasCISA = false;

  // First pass: identify what needs consolidation and preserve non-consolidated options
  for (const option of options) {
    // Preserve group options (Chariot group)
    if ('isGroup' in option && option.isGroup) {
      result.push(option);
      continue;
    }

    const value = String(option.value || '').toLowerCase();

    // Check if this is an NSA integration
    if (NSA_CSAAS_INTEGRATIONS.map(id => id.toLowerCase()).includes(value)) {
      hasNSA = true;
      continue; // Skip individual NSA options
    }

    // Check if this is a CISA integration
    if (CISA_CH_INTEGRATIONS.map(id => id.toLowerCase()).includes(value)) {
      hasCISA = true;
      continue; // Skip individual CISA options
    }

    // Preserve all other options
    result.push(option);
  }

  // Second pass: add consolidated options if needed
  if (hasNSA) {
    result.push({
      label: NSA_CSAAS_DISPLAY,
      value: NSA_CSAAS_FILTER_VALUE,
      id: NSA_CSAAS_FILTER_VALUE,
    });
  }

  if (hasCISA) {
    result.push({
      label: CISA_CH_DISPLAY,
      value: CISA_CH_FILTER_VALUE,
      id: CISA_CH_FILTER_VALUE,
    });
  }

  // Sort alphabetically (preserving groups at top)
  const groups = result.filter(opt => 'isGroup' in opt && opt.isGroup);
  const nonGroups = result.filter(opt => !('isGroup' in opt && opt.isGroup));
  nonGroups.sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  return [...groups, ...nonGroups];
};
